// Form Data Storage Module
// Handles loading form data from session/draft and updating iframe

class FormDataStorage {
    constructor() {
        this.currentData = {};
        this.init();
    }

    init() {
        // Load existing data from session/draft on page load
        this.loadFormData();
        
        // Attach event listeners to all forms
        this.attachFormListeners();
        
        // Attach real-time iframe update listeners
        setTimeout(() => {
            this.attachRealtimeIframeUpdates();
        }, 1500); // Wait for DOM to be fully ready

        // Attach inline editor for iframe elements
        this._iframeInlineEditorPopup = null;
        setTimeout(() => {
            this.attachIframeInlineEditor();
        }, 2000); // Wait for iframe to be ready
        
        // No auto-save to localStorage - data is managed server-side
    }

    // Get current form data from memory (no localStorage)
    getStoredData() {
        return this.currentData || {};
    }

    // Get form data from embedded page data (no API call needed)
    getEmbeddedFormData() {
        try {
            // Check if data is embedded in the page
            const embeddedData = window.storedInvitationData;
            // console.log('Checking for embedded form data on page...', embeddedData ? 'Data found' : 'No data found');
            
            if (embeddedData && embeddedData.success && embeddedData.data) {
                // Combine all sections into flat structure
                const combinedData = embeddedData.data;
                // console.log('Loaded form data from', embeddedData.source || 'page', ':', combinedData);
                return combinedData;
            }
            
            // console.log('No stored form data found on page');
            return {};
        } catch (error) {
            console.error('Error getting embedded form data:', error);
            return {};
        }
    }

    // Save form data to memory and update iframe (no localStorage)
    saveFormData(formData) {
        try {
            const existingData = this.getStoredData();
            // console.log('Existing form data in memory:', existingData);
            // console.log('Saving new form data:', formData);
            const updatedData = { ...existingData, ...formData };
            this.currentData = updatedData;
            // console.log('Form data saved to memory:', updatedData);
            
            // Update iframe elements with matching data-key-uid
            // this.updateIframeData(updatedData, formData);
            
            // Trigger custom event for other modules
            window.dispatchEvent(new CustomEvent('formDataSaved', { 
                detail: { data: updatedData, newData: formData } 
            }));
        } catch (error) {
            console.error('Error saving form data:', error);
        }
    }

    // Update iframe elements with data-key-uid attributes
    updateIframeData(allData, newData) {
        try {
            const themeIframe = document.getElementById('theme-iframe');
            const iframeDoc = themeIframe?.contentDocument || themeIframe?.contentWindow?.document || null;

            // Collect all [data-key-uid] elements: inside iframe + in parent page
            const elementsWithUid = [
                ...(iframeDoc ? Array.from(iframeDoc.querySelectorAll('[data-key-uid]')) : []),
                ...Array.from(document.querySelectorAll('[data-key-uid]'))
            ];
            if (!elementsWithUid.length) return;

            const themeDataFields = Array.isArray(window.themeDataFields) ? window.themeDataFields : [];
            const mediaFiles = (newData && newData.files) || (allData && allData.files) || {};

            elementsWithUid.forEach(element => {
                const uid = element.getAttribute('data-key-uid');
                if (!uid) return;

                // Look up field metadata by uid (UUID) OR by field name (e.g. "your_name").
                // themeDataFields only contains theme-specific custom data points, so standard
                // fields like your_name / partner_name may not be present — handled below.
                const fieldMeta = themeDataFields.find(f => f.uid === uid)
                               || themeDataFields.find(f => f.field === uid);

                if (fieldMeta) {
                    // ── Typed field from themeDataFields ──────────────────────────
                    const dataField  = fieldMeta.field;
                    const dataFormat = fieldMeta.format;
                    const dataType   = fieldMeta.type;
                    const dataUid    = fieldMeta.uid;

                    let fieldValue = null;
                    if (['month', 'day', 'date', 'year'].includes(dataField)) {
                        fieldValue = allData['event_date'] || null;
                    } else {
                        fieldValue = allData[dataField] || allData[uid] || allData[dataUid] || null;
                        if (!fieldValue) fieldValue = fieldMeta.default_value || null;
                    }

                    if (fieldValue) {
                        if (['media', 'image', 'video'].includes(dataType)) {
                            element.src = mediaFiles[dataUid] || fieldValue;
                            if (element.tagName.toLowerCase() === 'video') element.load();
                        } else if (dataType === 'text') {
                            if (dataField === 'address') {
                                const vn = allData['venue_name'] || null;
                                const va = allData['venue_address'] || null;
                                if (vn && va)       element.innerHTML = `${vn}, ${va}`;
                                else if (vn)        element.innerHTML = vn;
                                else if (va)        element.innerHTML = va;
                                else                element.innerHTML = this.formatText(fieldValue, dataFormat) || fieldValue;
                            } else {
                                element.innerHTML = this.formatText(fieldValue, dataFormat) || fieldValue;
                            }
                        } else if (['date', 'day', 'month', 'year'].includes(dataType)) {
                            const today = new Date();
                            const inputDate = new Date(fieldValue);
                            if (isNaN(inputDate) || inputDate < today) fieldValue = today.toISOString().split('T')[0];
                            element.innerHTML = this.formatDate(fieldValue, dataFormat, dataType) || fieldValue;
                        } else if (dataType === 'time') {
                            element.innerHTML = this.formatTime(fieldValue, dataFormat) || fieldValue;
                        }
                    } else if (fieldMeta.default_value) {
                        if (['media', 'image', 'video'].includes(dataType)) {
                            element.src = mediaFiles[dataUid] || fieldMeta.default_value;
                            if (element.tagName.toLowerCase() === 'video') element.load();
                        } else {
                            element.innerHTML = fieldMeta.default_value;
                        }
                    }

                } else {
                    // ── Standard / non-theme field (your_name, partner_name, event_title, etc.) ──
                    // uid is directly the field key stored in allData
                    const DATE_KEYS = ['event_date', 'date', 'day', 'month', 'year'];
                    let fieldValue = null;

                    if (DATE_KEYS.includes(uid)) {
                        fieldValue = allData['event_date'] || allData[uid] || null;
                    } else if (uid === 'event_time') {
                        fieldValue = allData['event_time'] || null;
                    } else if (uid === 'address') {
                        const vn = allData['venue_name'] || null;
                        const va = allData['venue_address'] || null;
                        if (vn && va) fieldValue = `${vn}, ${va}`;
                        else fieldValue = vn || va || null;
                    } else {
                        fieldValue = allData[uid] || null;
                    }

                    if (fieldValue) element.innerHTML = fieldValue;
                }
            });
        } catch (error) {
            console.error('Error updating iframe data:', error);
        }
    }

    // Format text based on format type
    formatText(value, format) {
        if (!value) return '';
        if (!format || format === 'default') return value;
        
        switch (format) {
            case 'initial':
                return value.trim().charAt(0).toUpperCase();
            case 'uppercase':
                return value.toUpperCase();
            case 'lowercase':
                return value.toLowerCase();
            case 'capitalize':
                return value.trim().charAt(0).toUpperCase() + value.trim().slice(1).toLowerCase();
            default:
                return value;
        }
    }

    // Format date based on format type
    formatDate(value, format, type) {
        // console.log('Formatting date:', value, format, type);
        if (!value) return '';
        
        let dateObj;
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
            dateObj = new Date(value + 'T00:00:00');
        } else if (!isNaN(value)) {
            if (type === 'year') {
                dateObj = new Date(value, 0, 1);
                // console.log('Year dateObj:', dateObj);
            } else if (type === 'month') {
                dateObj = new Date(2000, value - 1, 1);
                // console.log('Month dateObj:', dateObj);
            } else if (type === 'day' || type === 'date') {
                dateObj = new Date(2000, 0, value);
                // console.log('Day/Date dateObj:', dateObj);
            }
        } else {
            dateObj = new Date(value);
        }

        if (!dateObj || isNaN(dateObj)) return value;

        // Handle different date types
        if (type === 'date') {
            const day = dateObj.getDate();
            switch (format) {
                case 'numeric':
                    return `${day}`;
                case 'two_digit':
                    return day < 10 ? `0${day}` : `${day}`;
                case 'ordinal':
                    return this.getOrdinalSuffix(day);
                case 'ordinal_written':
                    return this.getWrittenOrdinal(day);
                case 'roman':
                    return this.toRomanNumeral(day);
                default:
                    return `${day}`;
            }
        } else if (type === 'day') {
            switch (format) {
                case 'full':
                    return dateObj.toLocaleDateString('en-US', { weekday: 'long' });
                case 'short':
                    return dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                default:
                    return dateObj.toLocaleDateString('en-US', { weekday: 'long' });
            }
        } else if (type === 'month') {
            switch (format) {
                case 'full':
                    return dateObj.toLocaleDateString('en-US', { month: 'long' });
                case 'short':
                    return dateObj.toLocaleDateString('en-US', { month: 'short' });
                case 'numeric':
                    return `${dateObj.getMonth() + 1}`;
                case 'two_digit':
                    return String(dateObj.getMonth() + 1).padStart(2, '0');
                default:
                    return dateObj.toLocaleDateString('en-US', { month: 'long' });
            }
        } else if (type === 'year') {
            switch (format) {
                case 'numeric':
                    return `${dateObj.getFullYear()}`;
                case 'two_digit':
                    return String(dateObj.getFullYear()).slice(-2);
                default:
                    return `${dateObj.getFullYear()}`;
            }
        }

        return value;
    }

    // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
    getOrdinalSuffix(n) {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }

    // Helper function to get written ordinal (First, Second, Third, etc.)
    getWrittenOrdinal(n) {
        const ordinals = [
            "First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth",
            "Eleventh", "Twelfth", "Thirteenth", "Fourteenth", "Fifteenth", "Sixteenth", "Seventeenth", 
            "Eighteenth", "Nineteenth", "Twentieth", "Twenty-first", "Twenty-second", "Twenty-third", 
            "Twenty-fourth", "Twenty-fifth", "Twenty-sixth", "Twenty-seventh", "Twenty-eighth", 
            "Twenty-ninth", "Thirtieth", "Thirty-first"
        ];
        return ordinals[n - 1] || n;
    }

    // Helper function to convert number to Roman numerals
    toRomanNumeral(num) {
        if (typeof num !== 'number') num = parseInt(num);
        if (isNaN(num)) return num;
        
        const lookup = {
            M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, 
            L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1
        };
        
        const roman = [];
        for (let i in lookup) {
            while (num >= lookup[i]) {
                roman.push(i);
                num -= lookup[i];
            }
        }
        return roman.join('');
    }

    // Format time based on format type
    formatTime(value, format) {
        if (!value) return '';
        
        const [hours, minutes] = value.split(':');
        const hour24 = parseInt(hours);
        const hour12 = hour24 % 12 || 12;
        const period = hour24 >= 12 ? 'PM' : 'AM';

        switch (format) {
            case '12hour':
                return `${hour12}:${minutes} ${period}`;
            case '24hour':
                return `${String(hour24).padStart(2, '0')}:${minutes}`;
            default:
                return `${hour12}:${minutes} ${period}`;
        }
    }

    // Load form data from embedded page data and populate form fields
    loadFormData() {
        // Get data from embedded page data (no API call)
        const storedData = this.getEmbeddedFormData();
        // console.log('Form data loaded for population:', storedData);
        
        if (Object.keys(storedData).length === 0) {
            // console.log('No stored form data found on page');
            return;
        }
        
        // Store in memory
        this.currentData = storedData;

        // Wait for DOM to be ready, then populate fields
        setTimeout(() => {
            // Populate form fields with data-theme-key attributes
            this.populateBasicFields(storedData);
            
            // Populate dynamic arrays (host, schedule, guests, etc.)
            this.populateDynamicArrayData(storedData);
            
            // Populate payment data if exists
            if (storedData.payment) {
                this.loadPaymentData(storedData.payment);
            }
            
            // Also update iframe data on load
            this.updateIframeData(storedData, storedData);
        }, 1000); // Give time for all modules to load
    }

    // Populate basic form fields
    populateBasicFields(storedData) {
        // Find all form inputs with data-theme-key attribute
        const elementsWithThemeKey = document.querySelectorAll('[data-theme-key]');
        
        elementsWithThemeKey.forEach(element => {
            let themeKey = element.getAttribute('data-theme-key');
            if (!themeKey) return;

            if (element.type === 'file') {
                themeKey = `files.${themeKey}`;
            }

            // Media text inputs — their files live under storedData.files keyed by uid
            const mediaAttrType = element.getAttribute('data-media-type');
            if (mediaAttrType) {
                const fileUrl = storedData.files ? storedData.files[themeKey] : undefined;
                if (fileUrl) {
                    this.setElementValue(element, fileUrl);
                }
                return;
            }
            
            // Get value from stored data using the theme key
            let value = this.getNestedValue(storedData, themeKey);
            // console.log('Value from stored data for', themeKey, ':', value);
            
            if (value !== undefined && value !== null) {
                this.setElementValue(element, value);
            }
        });
        
        // Populate dynamic array data
        this.populateDynamicArrayData(storedData);
    }

    // Populate dynamic array data into the DOM
    populateDynamicArrayData(data) {
        const arrayContainers = document.querySelectorAll('[data-theme-array]');
        
        arrayContainers.forEach(container => {
            const arrayKey = container.getAttribute('data-theme-array');
            if (!arrayKey) return;
            
            const arrayData = this.getNestedValue(data, arrayKey);
            
            if (!Array.isArray(arrayData) || arrayData.length === 0) return;
            
            // Get the template for creating new items
            const templateSelector = container.getAttribute('data-array-template');
            let template = null;
            
            if (templateSelector) {
                template = document.querySelector(templateSelector);
            }
            
            // Clear existing dynamic items if specified
            const shouldClear = container.getAttribute('data-array-clear') === 'true';
            if (shouldClear) {
                container.querySelectorAll('[data-array-item]').forEach(item => item.remove());
            }
            
            // Find existing items or create new ones
            arrayData.forEach((itemData, index) => {
                let item = container.querySelector(`[data-array-item="${itemData.id || index}"]`);
                
                // If item doesn't exist and we have a template, create it
                if (!item && template) {
                    const clone = template.content.cloneNode(true);
                    const itemElement = clone.querySelector('[data-array-item]');
                    if (itemElement) {
                        itemElement.setAttribute('data-array-item', itemData.id || index);
                        container.appendChild(clone);
                        item = container.querySelector(`[data-array-item="${itemData.id || index}"]`);
                    }
                }
                
                if (!item) return; // Skip if item doesn't exist
                
                // Populate fields within this item
                Object.keys(itemData).forEach(fieldName => {
                    if (fieldName === 'id') return; // Skip the id field
                    
                    const field = item.querySelector(`[data-array-field="${fieldName}"]`);
                    if (!field) return;
                    
                    const fieldValue = itemData[fieldName];
                    
                    // Set value based on field type
                    if (field.type === 'checkbox') {
                        field.checked = Boolean(fieldValue);
                    } else if (field.type === 'radio') {
                        if (field.value === String(fieldValue)) {
                            field.checked = true;
                        }
                    } else if (field.tagName === 'SELECT') {
                        if (field.multiple && Array.isArray(fieldValue)) {
                            Array.from(field.options).forEach(option => {
                                option.selected = fieldValue.includes(option.value);
                            });
                        } else if (Array.isArray(fieldValue)) {
                            // Handle options array for radio/checkbox groups
                            fieldValue.forEach(optVal => {
                                const optField = item.querySelector(`[data-array-field="${fieldName}"][value="${optVal}"]`);
                                if (optField) {
                                    optField.checked = true;
                                }
                            });
                        } else {
                            field.value = fieldValue;
                        }
                    } else if (field.tagName === 'TEXTAREA') {
                        field.value = fieldValue;
                    } else {
                        // Check if field expects comma-separated values
                        const isArrayField = field.getAttribute('data-array-type') === 'comma-separated';
                        
                        if (isArrayField && Array.isArray(fieldValue)) {
                            // Convert array back to comma-separated string
                            field.value = fieldValue.join(', ');
                        } else if (Array.isArray(fieldValue)) {
                            // Default: join with comma
                            field.value = fieldValue.join(', ');
                        } else {
                            field.value = fieldValue;
                        }
                    }
                    
                    // Trigger change event
                    field.dispatchEvent(new Event('change', { bubbles: true }));
                });
            });
            
            // Dispatch custom event to notify that array data was loaded
            window.dispatchEvent(new CustomEvent('arrayDataLoaded', {
                detail: { arrayKey, data: arrayData }
            }));
        });
    }

    // Helper function to get nested object values
    getNestedValue(obj, path) {
        // First try direct property access
        if (obj.hasOwnProperty(path)) {
            return obj[path];
        }
        
        // Then try nested access (e.g., 'features.payment.enablePayments')
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : undefined;
        }, obj);
    }

    // Helper function to set element values based on type
    setElementValue(element, value) {
        try {
            // Special handling for media text inputs (image/video pickers in custom-media.html)
            const mediaInputType = element.getAttribute('data-media-type');
            if (mediaInputType && value) {
                const themeUid = element.getAttribute('data-theme-uid') || '';
                const previewCard = element.closest('.media-preview-card');
                if (previewCard) {
                    const previewContainer = previewCard.querySelector('.preview-container');
                    if (previewContainer) {
                        if (mediaInputType === 'video') {
                            previewContainer.innerHTML =
                                '<div class="preview-content video-preview">' +
                                    `<video src="${value}" data-key-uid="${themeUid}" class="preview-video" autoplay muted loop playsinline preload="auto"></video>` +
                                '</div>';
                        } else {
                            previewContainer.innerHTML =
                                '<div class="preview-content image-preview">' +
                                    `<img src="${value}" alt="Preview" data-key-uid="${themeUid}" class="preview-image">` +
                                '</div>';
                        }
                    }
                    // Show delete button
                    const deleteBtn = previewCard.querySelector('.media-delete-button');
                    if (deleteBtn) deleteBtn.setAttribute('data-show-btn', 'true');
                    // Show mute button for video
                    if (mediaInputType === 'video') {
                        const muteBtn = previewCard.querySelector('.media-mute-button');
                        if (muteBtn) muteBtn.setAttribute('data-show-btn', 'true');
                    }
                }
                // Mark input as having an existing server file
                element.setAttribute('data-file-action', 'replace');
                element.value = value;
                return;
            }

            // Skip file inputs - browsers don't allow programmatically setting file input values
            if (element.type === 'file') {
                // console.log('File input :', element.getAttribute('data-file-action'), ' - value from data:', value);
                // Set data-file-action attribute to 'replace'
                if (value) {
                    element.setAttribute('data-file-action', 'replace');
                } else {
                    element.setAttribute('data-file-action', 'upload');
                }
                // console.log('File input - set data-file-action to', element.getAttribute('data-file-action'));
            } else if (element.type === 'checkbox') {
                element.checked = Boolean(value);
            } else if (element.type === 'radio') {
                if (element.value === value) {
                    element.checked = true;
                }
            } else if (element.tagName === 'SELECT') {
                // For select elements, check if the option exists
                const option = element.querySelector(`option[value="${value}"]`);
                if (option) {
                    element.value = value;
                }
            } else {
                element.value = value;
            }
            
            // Trigger change event to ensure any listeners are notified
            element.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (error) {
            console.warn(`Error setting value for element ${element.name || element.id}:`, error);
        }
    }

    // Load and rebuild RSVP form
    loadAndRebuildRSVP(rsvpData) {
        // Wait for RSVP feature to be loaded
        setTimeout(() => {
            if (window.loadSavedRSVPData) {
                window.loadSavedRSVPData();
            }
            
            // Also try to manually rebuild if RSVP questions exist
            if (rsvpData.questions && window.rebuildRSVPForm) {
                window.rsvpQuestions = rsvpData.questions;
                window.rsvpQuestionCounter = rsvpData.questionCounter || 1;
                window.rebuildRSVPForm();
            }
        }, 500);
    }

    // Extract all form data from the page
    extractAllFormData() {
        const allData = {};
        
        // Extract data only from elements with data-theme-key attribute
        this.extractThemeKeyFormFields(allData);

        return allData;
    }

    // Extract form data only from inputs with data-theme-key attribute
    extractThemeKeyFormFields(allData) {
        // Get all elements with data-theme-key attribute
        const elementsWithThemeKey = document.querySelectorAll('[data-theme-key]');
        
        elementsWithThemeKey.forEach(element => {
            const themeKey = element.getAttribute('data-theme-key');
            
            if (!themeKey) return; // Skip if data-theme-key is empty
            
            let value = null;
            
            // Handle different input types
            if (element.type === 'checkbox') {
                value = element.checked;
            } else if (element.type === 'radio') {
                if (element.checked) {
                    value = element.value;
                } else {
                    return; // Skip unchecked radio buttons
                }
            } else if (element.tagName === 'SELECT') {
                if (element.multiple) {
                    value = Array.from(element.selectedOptions).map(option => option.value);
                } else {
                    value = element.value;
                }
            } else if (element.tagName === 'TEXTAREA') {
                value = element.value;
            } else if (element.type === 'file') {
                // Handle file inputs - create blob URL
                if (element.files && element.files[0]) {
                    const file = element.files[0];
                    // console.log('File input changed, file:', file);
                    value = URL.createObjectURL(file);
                    // console.log('Created blob URL:', value);
                } else {
                    return; // Skip if no file selected
                }
            } else {
                value = element.value;
            }
            
            // Store with nested structure if themeKey contains dots
            this.setNestedValue(allData, themeKey, value);
        });
        
        // Extract dynamic array data (e.g., rsvpForm, payment items, etc.)
        this.extractDynamicArrayData(allData);
    }

    // Extract dynamic array data from data-theme-array attributes
    extractDynamicArrayData(allData) {
        // Find all containers with data-theme-array attribute
        const arrayContainers = document.querySelectorAll('[data-theme-array]');
        
        arrayContainers.forEach(container => {
            const arrayKey = container.getAttribute('data-theme-array');
            if (!arrayKey) return;
            
            const arrayData = [];
            
            // Find all items within this container
            const items = container.querySelectorAll('[data-array-item]');
            
            items.forEach(item => {
                const itemData = {};
                
                // Get the item index or id
                const itemId = item.getAttribute('data-array-item');
                if (itemId) {
                    itemData.id = itemId;
                }
                
                // Extract all fields within this item
                const fields = item.querySelectorAll('[data-array-field]');
                fields.forEach(field => {
                    const fieldName = field.getAttribute('data-array-field');
                    if (!fieldName) return;
                    
                    let fieldValue;
                    
                    // Handle different input types
                    if (field.type === 'checkbox') {
                        fieldValue = field.checked;
                    } else if (field.type === 'radio') {
                        if (field.checked) {
                            fieldValue = field.value;
                        } else {
                            return; // Skip unchecked radio buttons
                        }
                    } else if (field.tagName === 'SELECT') {
                        if (field.multiple) {
                            fieldValue = Array.from(field.selectedOptions).map(option => option.value);
                        } else {
                            fieldValue = field.value;
                        }
                    } else if (field.tagName === 'TEXTAREA') {
                        fieldValue = field.value;
                    } else {
                        fieldValue = field.value;
                        
                        // Check if field should be treated as comma-separated array
                        const isArrayField = field.getAttribute('data-array-type') === 'comma-separated';
                        if (isArrayField && fieldValue) {
                            // Convert comma-separated string to array
                            fieldValue = fieldValue.split(',').map(v => v.trim()).filter(v => v);
                        }
                    }
                    
                    // Support nested field names with dots
                    if (fieldName.includes('.')) {
                        const keys = fieldName.split('.');
                        let current = itemData;
                        for (let i = 0; i < keys.length - 1; i++) {
                            if (!current[keys[i]]) {
                                current[keys[i]] = {};
                            }
                            current = current[keys[i]];
                        }
                        current[keys[keys.length - 1]] = fieldValue;
                    } else {
                        itemData[fieldName] = fieldValue;
                    }
                });
                
                // Only add item if it has data
                if (Object.keys(itemData).length > 0) {
                    arrayData.push(itemData);
                }
            });
            
            // Store the array data
            if (arrayData.length > 0) {
                this.setNestedValue(allData, arrayKey, arrayData);
            }
        });
    }

    // Helper function to set nested object values
    setNestedValue(obj, path, value) {
        const keys = path.split('.');
        let current = obj;
        
        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (!current[key] || typeof current[key] !== 'object') {
                current[key] = {};
            }
            current = current[key];
        }
        
        current[keys[keys.length - 1]] = value;
    }

    // Extract payment form data
    extractPaymentData() {
        const paymentData = {};
        
        // Get payment enable/disable state
        const enablePayments = document.getElementById('enablePayments');
        if (enablePayments) {
            paymentData.enabled = enablePayments.checked;
        }
        
        // Get payment items
        const paymentItems = [];
        document.querySelectorAll('.payment-item').forEach((item, index) => {
            const itemData = {};
            const nameInput = item.querySelector(`#itemName${index + 1}`);
            const priceInput = item.querySelector(`#itemPrice${index + 1}`);
            const quantityInput = item.querySelector(`#itemQuantity${index + 1}`);
            const descInput = item.querySelector(`#itemDescription${index + 1}`);
            
            if (nameInput) itemData.name = nameInput.value;
            if (priceInput) itemData.price = priceInput.value;
            if (quantityInput) itemData.quantity = quantityInput.value;
            if (descInput) itemData.description = descInput.value;
            
            if (itemData.name) {
                paymentItems.push(itemData);
            }
        });
        
        paymentData.items = paymentItems;
        return paymentData;
    }

    // Extract gift registry form data
    extractGiftRegistryFormData() {
        const registryData = {
            registries: [],
            gifts: []
        };
        
        // Extract registries
        document.querySelectorAll('.registry-item').forEach(item => {
            const registryId = item.getAttribute('data-registry-id');
            if (registryId) {
                const registry = {
                    id: registryId,
                    store: item.querySelector(`#registryStore_${registryId}`)?.value || '',
                    url: item.querySelector(`#registryUrl_${registryId}`)?.value || '',
                    title: item.querySelector(`#registryTitle_${registryId}`)?.value || '',
                    description: item.querySelector(`#registryDescription_${registryId}`)?.value || ''
                };
                registryData.registries.push(registry);
            }
        });
        
        // Extract custom gifts
        document.querySelectorAll('.gift-item').forEach(item => {
            const giftId = item.getAttribute('data-gift-id');
            if (giftId) {
                const gift = {
                    id: giftId,
                    name: item.querySelector(`#giftName_${giftId}`)?.value || '',
                    price: item.querySelector(`#giftPrice_${giftId}`)?.value || '',
                    priority: item.querySelector(`#giftPriority_${giftId}`)?.value || 'medium',
                    url: item.querySelector(`#giftUrl_${giftId}`)?.value || '',
                    store: item.querySelector(`#giftStore_${giftId}`)?.value || '',
                    category: item.querySelector(`#giftCategory_${giftId}`)?.value || '',
                    description: item.querySelector(`#giftDescription_${giftId}`)?.value || '',
                    allowMultiple: item.querySelector(`#giftMultiple_${giftId}`)?.checked || false,
                    allowGroupBuy: item.querySelector(`#giftGroupBuy_${giftId}`)?.checked || false,
                    highPriority: item.querySelector(`#giftHighPriority_${giftId}`)?.checked || false
                };
                registryData.gifts.push(gift);
            }
        });
        
        return registryData;
    }

    // Extract form data from a specific form or element
    extractFormData(formElement) {
        const formData = {};
        const elements = formElement.querySelectorAll('[data-theme-key]');
        
        elements.forEach(element => {
            const themeKey = element.getAttribute('data-theme-key');
            if (!themeKey) return;
            
            let value;
            if (element.type === 'checkbox') {
                value = element.checked;
            } else if (element.type === 'radio') {
                if (element.checked) {
                    value = element.value;
                } else {
                    return; // Skip unchecked radio buttons
                }
            } else if (element.tagName === 'SELECT') {
                if (element.multiple) {
                    value = Array.from(element.selectedOptions).map(option => option.value);
                } else {
                    value = element.value;
                }
            } else if (element.type === 'file') {
                // Handle file inputs - create blob URL
                if (element.files && element.files[0]) {
                    const file = element.files[0];
                    // console.log('File input changed in extractFormData, file:', file);
                    value = URL.createObjectURL(file);
                    // console.log('Created blob URL:', value);
                } else {
                    return; // Skip if no file selected
                }
            } else {
                value = element.value;
            }
            
            if (value !== undefined) {
                // Store with nested structure if themeKey contains dots
                this.setNestedValue(formData, themeKey, value);
            }
        });
        
        return formData;
    }

    // Load payment data into forms
    loadPaymentData(paymentData) {
        if (paymentData.enabled !== undefined) {
            const enablePayments = document.getElementById('enablePayments');
            if (enablePayments) {
                enablePayments.checked = paymentData.enabled;
                enablePayments.dispatchEvent(new Event('change'));
            }
        }
        
        // Load payment items if they exist
        if (paymentData.items && Array.isArray(paymentData.items)) {
            paymentData.items.forEach((item, index) => {
                // Try to find existing payment item or create new ones
                const nameInput = document.querySelector(`#itemName${index + 1}`);
                const priceInput = document.querySelector(`#itemPrice${index + 1}`);
                const quantityInput = document.querySelector(`#itemQuantity${index + 1}`);
                const descInput = document.querySelector(`#itemDescription${index + 1}`);
                
                if (nameInput) nameInput.value = item.name || '';
                if (priceInput) priceInput.value = item.price || '';
                if (quantityInput) quantityInput.value = item.quantity || '';
                if (descInput) descInput.value = item.description || '';
            });
        }
    }

    // Attach form submission listeners
    attachFormListeners() {
        document.addEventListener('submit', (e) => {
            if (e.target.classList.contains('edit-form')) {
                const formData = this.extractAllFormData();
                // console.log('Form submitted, saving form data:', formData);
                this.saveFormData(formData);
            }
        });
    }

    // Attach input change listeners with debouncing (removed - no auto-save to server)
    // attachInputListeners() {
    //     // No longer needed - form data is saved on form submission and tab navigation only
    // }

    /**
     * Attach real-time input change listeners to update iframe dynamically
     * Handles inputs with data-theme-key or data-theme-uid attributes
     */
    attachRealtimeIframeUpdates() {
        // Get all inputs with data-theme-key or data-theme-uid
        const inputsToWatch = document.querySelectorAll('[data-theme-key], [data-theme-uid]');
        
        inputsToWatch.forEach(input => {
            input.addEventListener('change', (e) => {
                this.handleInputChangeForIframe(e.target);
            });

            // Also listen to 'input' for text fields for real-time updates
            if (input.type === 'text' || input.tagName === 'TEXTAREA') {
                input.addEventListener('input', (e) => {
                    this.handleInputChangeForIframe(e.target);
                });
            }
        });

        // console.log(`Attached real-time iframe updates to ${inputsToWatch.length} input fields`);
    }

    /**
     * Handle input change and update iframe elements with matching data-key-uid
     * @param {HTMLElement} input - The input element that changed
     */
    handleInputChangeForIframe(input) {
        try {
            const themeKey = input.getAttribute('data-theme-key');
            const themeUid = input.getAttribute('data-theme-uid');

            // console.log('Handling input change for iframe update: Key: ', themeKey, 'UID:', themeUid);
            
            if (!themeKey && !themeUid) return;

            // Get input value early so date/time special cases can use it
            let inputValue = null;
            if (input.type === 'checkbox') {
                inputValue = input.checked;
            } else if (input.type === 'file') {
                if (input.files && input.files[0]) {
                    inputValue = URL.createObjectURL(input.files[0]);
                } else {
                    console.warn('No file selected');
                    return;
                }
            } else {
                inputValue = input.value;
            }

            // Allow boolean false (unchecked checkbox) to propagate; only skip null/undefined/empty string
            if (inputValue === null || inputValue === undefined || (inputValue === '' && input.type !== 'checkbox')) return;

            // Special handling for event_date: update all date/day/month/year fields
            // Match by f.type OR f.field — themes sometimes declare year/month/day with type:"text"
            if (themeKey === 'event_date') {
                const DATE_FIELD_NAMES = ['date', 'day', 'month', 'year'];
                const fields = Array.isArray(window.themeDataFields) ? window.themeDataFields : [];
                const dateTypeFields = fields.filter(f =>
                    DATE_FIELD_NAMES.includes(f.type) || DATE_FIELD_NAMES.includes(f.field)
                );
                dateTypeFields.forEach(f => {
                    const effectiveType = DATE_FIELD_NAMES.includes(f.type) ? f.type : f.field;
                    this.updateIframeElementByUid(f.uid, inputValue, effectiveType, f.format, f.field);
                });
                this.saveFormData({ event_date: inputValue });
                return;
            }

            // Special handling for event_time: update all time fields
            if (themeKey === 'event_time') {
                const fields = Array.isArray(window.themeDataFields) ? window.themeDataFields : [];
                const timeFields = fields.filter(f => f.type === 'time');
                timeFields.forEach(f => {
                    this.updateIframeElementByUid(f.uid, inputValue, f.type, f.format, f.field);
                });
                this.saveFormData({ event_time: inputValue });
                return;
            }

            // Find ALL themeDataFields entries for this field key — there may be multiple
            // UUID-based elements that share the same field name (e.g. your_name with format "initial").
            const fields = Array.isArray(window.themeDataFields) ? window.themeDataFields : [];
            let matchingFields = themeKey ? fields.filter(f => f.field === themeKey) : [];
            if (!matchingFields.length && themeUid) {
                const fm = fields.find(f => f.uid === themeUid) || fields.find(f => f.field === themeUid);
                if (fm) matchingFields = [fm];
            }

            if (matchingFields.length > 0) {
                // Update every UUID-mapped element (each call also updates [data-key-uid="${field}"] variants)
                matchingFields.forEach(fm => {
                    this.updateIframeElementByUid(fm.uid, inputValue, fm.type, fm.format, fm.field);
                });
            } else {
                // No themeDataFields entry — update elements that use the field name directly as data-key-uid.
                // Still save the value so feature toggles / venue_name / rsvp_enabled etc. persist.
                if (themeKey) {
                    this.updateIframeElementByUid(themeKey, inputValue, 'text', null, null);
                }
            }

            if (themeKey) {
                this.saveFormData({ [themeKey]: inputValue });
            }

        } catch (error) {
            console.error('Error handling input change for iframe:', error);
        }
    }

    /**
     * Update iframe element with specific UID
     * @param {string} uid - The data-key-uid to target
     * @param {any} value - The value to set
     * @param {string} dataType - The data type (text, date, media, etc.)
     * @param {string} dataFormat - The format for the data type
     */
    updateIframeElementByUid(uid, value, dataType, dataFormat, fieldName = null) {
        try {
            const themeIframe = document.getElementById('theme-iframe');
            const iframeDoc = themeIframe?.contentDocument || themeIframe?.contentWindow?.document || null;
            if (!iframeDoc) {
                console.warn('Iframe not accessible for real-time update - updating outside iframe only');
            }

            // Find elements matching by UID or field name (templates may use either)
            const iframeElements = iframeDoc ? [
                ...Array.from(iframeDoc.querySelectorAll(`[data-key-uid="${uid}"]`)),
                ...(fieldName && fieldName !== uid ? Array.from(iframeDoc.querySelectorAll(`[data-key-uid="${fieldName}"]`)) : [])
            ] : [];
            const uidElements = [
                ...Array.from(document.querySelectorAll(`[data-key-uid="${uid}"]`)),
                ...(fieldName && fieldName !== uid ? Array.from(document.querySelectorAll(`[data-key-uid="${fieldName}"]`)) : [])
            ];

            // Merge and deduplicate
            const targetElements = [...new Set(iframeElements.concat(uidElements))];
            
            if (!targetElements.length) {
                console.warn('No iframe element found with data-key-uid:', uid);
                return;
            }

            // console.log(`Updating ${targetElements.length} iframe elements with data-key-uid: ${uid}`);

            // Handle different data types
            targetElements.forEach(iframeElement => {
                if (['media', 'image', 'video'].includes(dataType)) {
                    // Media type - update src attribute
                    iframeElement.src = value;
                    // Play the media if it's a video element
                    // console.log('TagName of iframe element:', iframeElement.tagName);
                    if (iframeElement.tagName === 'VIDEO') {
                        iframeElement.load();
                    }
                    // console.log('✅ Updated iframe media src');
                } else if (dataType === 'text') {
                    // Text type - update innerHTML with formatting
                    const formattedValue = this.formatText(value, dataFormat);
                    iframeElement.innerHTML = formattedValue || value;
                    // console.log('✅ Updated iframe text content');
                } else if (['date', 'day', 'month', 'year'].includes(dataType)) {
                    // Date type - update innerHTML with date formatting
                    const formattedValue = this.formatDate(value, dataFormat, dataType);
                    iframeElement.innerHTML = formattedValue || value;
                    // console.log('✅ Updated iframe date content');
                } else if (dataType === 'time') {
                    // Time type - update innerHTML with time formatting
                    const formattedValue = this.formatTime(value, dataFormat);
                    iframeElement.innerHTML = formattedValue || value;
                    // console.log('✅ Updated iframe time content');
                } else {
                    // Default - just set innerHTML
                    iframeElement.innerHTML = value;
                    // console.log('✅ Updated iframe content (default)');
                }
            });

        } catch (error) {
            console.error('Error updating iframe element by UID:', error);
        }
    }

    /**
     * Resolve field metadata from window.themeDataFields
     * @param {string} themeKey - The data-theme-key attribute value
     * @param {string} themeUid - The data-theme-uid attribute value
     * @returns {Object|null} Field metadata object or null
     */
    resolveFieldMetadata(themeKey, themeUid) {
        try {
            const fields = Array.isArray(window.themeDataFields) ? window.themeDataFields : [];
            if (!fields.length) return null;

            // console.log('Resolving field metadata:', themeKey, themeUid);
            // console.log('Available theme data fields:', fields);

            // Match by field name first (data-theme-key="your_name")
            if (themeKey) {
                const matchByField = fields.find(f => f.field === themeKey);
                if (matchByField) return matchByField;
            }

            // Then by exact uid match (data-theme-uid="<uuid>")
            if (themeUid) {
                const matchByUid = fields.find(f => f.uid === themeUid);
                if (matchByUid) return matchByUid;
                // Also try uid-as-field-name (data-theme-uid="your_name")
                const matchByUidAsField = fields.find(f => f.field === themeUid);
                if (matchByUidAsField) return matchByUidAsField;
            }

            return null;
        } catch (err) {
            // console.warn('Unable to resolve field metadata:', err);
            return null;
        }
    }

    // ─── Iframe Inline Editor ────────────────────────────────────────────────────

    /**
     * Attach a click-to-edit popup for non-media elements inside the iframe.
     * Text → text input, date/day/month/year → date input, time → time input.
     */
    attachIframeInlineEditor() {
        const themeIframe = document.getElementById('theme-iframe');
        if (!themeIframe) return;

        // Create and stash the popup in the main document once
        if (!this._iframeInlineEditorPopup) {
            this._iframeInlineEditorPopup = this._createInlineEditorPopup();
            document.body.appendChild(this._iframeInlineEditorPopup);
        }
        const popup = this._iframeInlineEditorPopup;

        const tryAttach = () => {
            const iframeDoc = themeIframe.contentDocument || themeIframe.contentWindow?.document;
            if (!iframeDoc) return;

            // Inject cursor/highlight CSS into iframe so clickable fields are obvious
            const styleId = 'iie-cursor-style';
            if (!iframeDoc.getElementById(styleId)) {
                const s = iframeDoc.createElement('style');
                s.id = styleId;
                s.textContent = `
                    [data-key-uid]:not([data-key-uid=""]) {
                        cursor: pointer !important;
                        transition: outline 0.15s;
                    }
                    [data-key-uid]:not([data-key-uid=""]):hover {
                        outline: 2px dashed rgba(218,165,32,0.7) !important;
                        outline-offset: 2px !important;
                        border-radius: 2px !important;
                    }
                `;
                (iframeDoc.head || iframeDoc.body).appendChild(s);
            }

            // Click handler inside iframe
            iframeDoc.addEventListener('click', (e) => {
                const target = e.target.closest('[data-key-uid]');
                if (!target) {
                    this._hideInlineEditorPopup(popup);
                    return;
                }

                const uid = target.getAttribute('data-key-uid');
                if (!uid) return;

                const fields = Array.isArray(window.themeDataFields) ? window.themeDataFields : [];
                const fieldMeta = fields.find(f => f.uid === uid) || fields.find(f => f.field === uid);
                if (!fieldMeta) return;

                // Skip media/image/video – handled separately
                if (['media', 'image', 'video'].includes(fieldMeta.type)) return;

                e.preventDefault();
                e.stopPropagation();

                // Resolve current value
                const storedData = this.getStoredData();
                let currentValue = '';
                if (['date', 'day', 'month', 'year'].includes(fieldMeta.type)) {
                    currentValue = storedData['event_date'] || storedData[fieldMeta.field] || '';
                } else if (fieldMeta.type === 'time') {
                    currentValue = storedData['event_time'] || storedData[fieldMeta.field] || '';
                } else {
                    currentValue = storedData[fieldMeta.field] || storedData[fieldMeta.uid] || target.textContent.trim() || '';
                }

                this._showInlineEditorPopup(popup, target, themeIframe, fieldMeta, currentValue, uid);
            });
        };

        // Attach after iframe is ready
        const iframeDoc = themeIframe.contentDocument || themeIframe.contentWindow?.document;
        if (iframeDoc && iframeDoc.readyState === 'complete') {
            tryAttach();
        } else {
            themeIframe.addEventListener('load', tryAttach);
        }

        // Hide popup when clicking anywhere in the main document (outside popup)
        document.addEventListener('click', (e) => {
            if (popup && !popup.contains(e.target)) {
                this._hideInlineEditorPopup(popup);
            }
        });
    }

    /** Build the popup DOM once and return it (hidden by default) */
    _createInlineEditorPopup() {
        const popup = document.createElement('div');
        popup.id = 'iframeInlineEditorPopup';
        popup.setAttribute('role', 'dialog');
        popup.setAttribute('aria-label', 'Inline editor');
        Object.assign(popup.style, {
            position: 'fixed',
            zIndex: '999999',
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
            padding: '14px 16px 12px',
            minWidth: '260px',
            maxWidth: '340px',
            display: 'none',
            flexDirection: 'column',
            gap: '8px',
            border: '1px solid #e0e0e0',
        });

        popup.innerHTML = `
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:2px;">
                <span id="iieLabel" style="font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.6px;">Edit field</span>
                <button id="iieCloseBtn" type="button" aria-label="Close"
                    style="background:none;border:none;cursor:pointer;padding:2px 6px;color:#aaa;font-size:20px;line-height:1;">&times;</button>
            </div>
            <div id="iieInputWrapper"></div>
            <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:4px;">
                <button id="iieDoneBtn" type="button"
                    style="background:#DAA520;color:#fff;border:none;border-radius:7px;padding:7px 20px;font-size:13px;font-weight:600;cursor:pointer;letter-spacing:0.2px;">
                    Done
                </button>
            </div>
        `;
        return popup;
    }

    /**
     * Position & populate the popup for the given iframe element.
     * @param {HTMLElement} popup
     * @param {HTMLElement} targetElement - element inside iframe that was clicked
     * @param {HTMLIFrameElement} iframe
     * @param {Object} fieldMeta
     * @param {string} currentValue
     * @param {string} uid
     */
    _showInlineEditorPopup(popup, targetElement, iframe, fieldMeta, currentValue, uid) {
        const inputWrapper = popup.querySelector('#iieInputWrapper');
        const label = popup.querySelector('#iieLabel');

        const DATE_TYPES = ['date', 'day', 'month', 'year'];
        const isDateGroup = DATE_TYPES.includes(fieldMeta.type);
        const isTime = fieldMeta.type === 'time';

        // Label: date/time groups get a friendly title; text fields use field name
        if (isDateGroup) {
            label.textContent = 'Event Date';
        } else if (isTime) {
            label.textContent = 'Event Time';
        } else {
            label.textContent = (fieldMeta.field || fieldMeta.uid || 'field').replace(/_/g, ' ');
        }

        // ── Build input ────────────────────────────────────────────────────────
        inputWrapper.innerHTML = '';
        const inputEl = document.createElement('input');
        const inputStyle = 'width:100%;padding:9px 11px;border:1.5px solid #ddd;border-radius:8px;font-size:14px;outline:none;box-sizing:border-box;transition:border-color 0.15s;';
        inputEl.style.cssText = inputStyle;
        inputEl.addEventListener('focus', () => { inputEl.style.borderColor = '#DAA520'; });
        inputEl.addEventListener('blur', () => { inputEl.style.borderColor = '#ddd'; });

        if (isDateGroup) {
            inputEl.type = 'date';
            // Resolve the canonical date from storage (always stored under key "event_date")
            const stored = this.getStoredData();
            const storedDate = stored['event_date'] || currentValue || '';
            inputEl.value = /^\d{4}-\d{2}-\d{2}$/.test(storedDate) ? storedDate : '';
        } else if (isTime) {
            inputEl.type = 'time';
            const stored = this.getStoredData();
            inputEl.value = stored['event_time'] || currentValue || '';
        } else {
            inputEl.type = 'text';
            inputEl.placeholder = `Enter ${label.textContent}…`;
            inputEl.value = currentValue || '';
        }
        inputWrapper.appendChild(inputEl);

        // ── Position popup relative to target element ──────────────────────────
        popup.style.display = 'flex';
        const iframeRect = iframe.getBoundingClientRect();
        const elemRect = targetElement.getBoundingClientRect(); // relative to iframe viewport

        const popupW = 280;
        const popupH = popup.offsetHeight || 130;

        let top = iframeRect.top + elemRect.top + elemRect.height + 8;
        let left = iframeRect.left + elemRect.left;

        // Clamp horizontal
        if (left + popupW > window.innerWidth - 10) left = window.innerWidth - popupW - 10;
        if (left < 10) left = 10;
        // Flip up if not enough space below
        if (top + popupH > window.innerHeight - 10) top = iframeRect.top + elemRect.top - popupH - 8;
        if (top < 10) top = 10;

        popup.style.top = `${top}px`;
        popup.style.left = `${left}px`;

        // Focus the input
        setTimeout(() => {
            inputEl.focus();
            if (inputEl.type === 'text') inputEl.select();
        }, 40);

        // ── Wire up handlers (replace buttons to avoid stale listeners) ────────
        const replaceBtn = (id) => {
            const old = popup.querySelector(`#${id}`);
            const fresh = old.cloneNode(true);
            old.parentNode.replaceChild(fresh, old);
            return fresh;
        };
        const closeBtn = replaceBtn('iieCloseBtn');
        const doneBtn = replaceBtn('iieDoneBtn');

        const applyChange = () => {
            const newValue = inputEl.value;
            if (!newValue) return;

            if (isDateGroup) {
                // Propagate the new date to ALL date-related fields + form inputs
                this._applyDateToAllRelatedFields(newValue);
            } else if (isTime) {
                // Propagate the new time to ALL time-related fields + form inputs
                this._applyTimeToAllRelatedFields(newValue);
            } else {
                // Plain text field: update iframe + sync form inputs + save
                this.updateIframeElementByUid(uid, newValue, fieldMeta.type, fieldMeta.format);
                this._syncPopupValueToFormInputs(uid, fieldMeta, newValue);
                if (fieldMeta.field) {
                    this.saveFormData({ [fieldMeta.field]: newValue });
                }
            }
        };

        // Real-time update while typing / picking
        inputEl.addEventListener('input', applyChange);
        inputEl.addEventListener('change', applyChange);

        doneBtn.addEventListener('click', () => {
            applyChange();
            this._hideInlineEditorPopup(popup);
        });

        closeBtn.addEventListener('click', () => {
            this._hideInlineEditorPopup(popup);
        });

        inputEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { applyChange(); this._hideInlineEditorPopup(popup); }
            if (e.key === 'Escape') { this._hideInlineEditorPopup(popup); }
        });
    }

    /**
     * Propagate a YYYY-MM-DD date to every date/day/month/year UID in the
     * iframe, all matching form inputs, and save to storage.
     * @param {string} dateValue - YYYY-MM-DD
     */
    _applyDateToAllRelatedFields(dateValue) {
        if (!dateValue) return;
        const DATE_TYPES = ['date', 'day', 'month', 'year'];
        const allFields = Array.isArray(window.themeDataFields) ? window.themeDataFields : [];

        // 1. Update every date-related UID in the iframe
        // Match by f.type OR f.field — themes sometimes declare year/month/day with type:"text"
        const dateFields = allFields.filter(f => DATE_TYPES.includes(f.type) || DATE_TYPES.includes(f.field));
        dateFields.forEach(f => {
            const effectiveType = DATE_TYPES.includes(f.type) ? f.type : f.field;
            this.updateIframeElementByUid(f.uid, dateValue, effectiveType, f.format);
        });

        // 2. Update the canonical date form input (data-theme-key="event_date") and any
        //    other inputs that are logically bound to any date sub-type UID
        const seen = new Set();
        const syncInput = (input) => {
            if (!input || seen.has(input) || input.type === 'file') return;
            seen.add(input);
            input.value = dateValue;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        };

        // Main date input by theme-key
        document.querySelectorAll('[data-theme-key="event_date"]').forEach(syncInput);

        // Any inputs bound to date sub-type UIDs
        dateFields.forEach(f => {
            document.querySelectorAll(`[data-theme-uid="${f.uid}"]`).forEach(syncInput);
        });

        // 3. Persist canonical date
        this.saveFormData({ event_date: dateValue });
    }

    /**
     * Propagate an HH:MM time to every time-type UID in the iframe,
     * all matching form inputs, and save to storage.
     * @param {string} timeValue - HH:MM
     */
    _applyTimeToAllRelatedFields(timeValue) {
        if (!timeValue) return;
        const allFields = Array.isArray(window.themeDataFields) ? window.themeDataFields : [];

        // 1. Update every time-related UID in the iframe
        const timeFields = allFields.filter(f => f.type === 'time');
        timeFields.forEach(f => {
            this.updateIframeElementByUid(f.uid, timeValue, f.type, f.format);
        });

        // Also update the clicked uid itself in case it's not in themeDataFields
        const seen = new Set();
        const syncInput = (input) => {
            if (!input || seen.has(input) || input.type === 'file') return;
            seen.add(input);
            input.value = timeValue;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        };

        // 2. Update form inputs
        document.querySelectorAll('[data-theme-key="event_time"]').forEach(syncInput);
        timeFields.forEach(f => {
            document.querySelectorAll(`[data-theme-uid="${f.uid}"]`).forEach(syncInput);
        });

        // 3. Persist
        this.saveFormData({ event_time: timeValue });
    }

    /**
     * Sync a value back to every sidebar form input that corresponds to the given uid/field.
     * Used for plain-text fields only (date/time use their dedicated helpers above).
     * @param {string} uid
     * @param {Object} fieldMeta
     * @param {string} value
     */
    _syncPopupValueToFormInputs(uid, fieldMeta, value) {
        const selectors = [];
        if (uid) selectors.push(`[data-theme-uid="${uid}"]`);
        if (fieldMeta.field) selectors.push(`[data-theme-key="${fieldMeta.field}"]`);

        if (!selectors.length) return;

        const inputs = Array.from(document.querySelectorAll(selectors.join(',')));
        // Deduplicate
        const seen = new Set();
        inputs.forEach(input => {
            if (seen.has(input)) return;
            seen.add(input);
            if (input.type === 'file') return;
            if (input.type === 'checkbox') {
                input.checked = Boolean(value);
            } else {
                input.value = value;
            }
            // Dispatch without re-triggering the inline-editor loop
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    /** Hide the inline editor popup */
    _hideInlineEditorPopup(popup) {
        if (popup) popup.style.display = 'none';
    }

    // ─────────────────────────────────────────────────────────────────────────────

    // Clear all stored data from memory
    clearStoredData() {
        this.currentData = {};
        // console.log('Form data cleared from memory');
    }

    // Export data as JSON from memory
    exportData() {
        const data = this.getStoredData();
        const dataStr = JSON.stringify(data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = 'eventic-form-data.json';
        link.click();
        URL.revokeObjectURL(url);
    }

    // Import data from JSON
    importData(jsonData) {
        try {
            const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
            // console.log('Imported data:', data);
            this.saveFormData(data);
            this.loadFormData();
        } catch (error) {
            console.error('Error importing data:', error);
        }
    }

    // RSVP-specific storage methods
    saveRSVPData(rsvpData) {
        const formData = {
            rsvp: {
                questions: rsvpData.questions || [],
                questionCounter: rsvpData.questionCounter || 1,
                settings: rsvpData.settings || {},
                lastUpdated: new Date().toISOString()
            }
        };
        this.saveFormData(formData);
    }

    async loadRSVPData() {
        const storedData = this.getStoredData();
        return storedData.rsvp || {
            questions: [],
            questionCounter: 1,
            settings: {}
        };
    }
}

// Make FormDataStorage globally available
window.FormDataStorage = FormDataStorage;

// Initialize form data storage
window.formDataStorage = new FormDataStorage();

// Make utility functions globally available
window.clearFormData = () => window.formDataStorage.clearStoredData();
window.exportFormData = () => window.formDataStorage.exportData();
window.importFormData = (data) => window.formDataStorage.importData(data);

// Helper function to update a single field
window.updateFormDataField = (key, value) => {
    // console.log(`Updating form field '${key}':`, value);
    const formData = { [key]: value };
    window.formDataStorage.saveFormData(formData);
    // console.log('Form data after update:', window.formDataStorage.getStoredData());
};

// Helper function to get a single field
window.getFormDataField = (key) => {
    const storedData = window.formDataStorage.getStoredData();
    return storedData[key];
};

